import React, { useEffect, useState } from 'react';
import {
  Card,
  Form,
  Input,
  DatePicker,
  Select,
  Button,
  Space,
  message,
  Typography,
  Row,
  Col,
  Checkbox,
  Table,
  InputNumber,
} from 'antd';
import { CheckOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import { quotationService, CreateQuotationData, Quotation } from '../services/quotations';
import { customerService } from '../services/customers';
import { invoiceService } from '../services/invoices';
import { teamService } from '../services/teams';
import { staffService, Staff } from '../services/staffs';
import { businessService } from '../services/businesses';
import { taskTypeService } from '../services/taskTypes';
import { taskTemplateService } from '../services/taskTemplates';
import dayjs, { Dayjs } from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

export const QuotationFormPage: React.FC = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [form] = Form.useForm();
  const { user } = useAuth();
  const canUpdate = hasPermission(user, 'quotations.update');
  const canDelete = hasPermission(user, 'quotations.delete');
  const [submitting, setSubmitting] = useState(false);
  const [loadingQuotation, setLoadingQuotation] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [customers, setCustomers] = useState<any[]>([]);
  const [businesses, setBusinesses] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [taskTypes, setTaskTypes] = useState<any[]>([]);
  const [taskTemplates, setTaskTemplates] = useState<any[]>([]);
  // const [tasks, setTasks] = useState<any[]>([]); // Commented out - data from task
  const [quotationItems, setQuotationItems] = useState<any[]>([]);
  const [customerType, setCustomerType] = useState<'personal' | 'business'>('personal');
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [selectedBusiness, setSelectedBusiness] = useState<any>(null);
  const [quotationName, setQuotationName] = useState<string>('');
  const [isEditing, setIsEditing] = useState(!isEdit); // For new quotations, default to editing mode
  const [currentStatus, setCurrentStatus] = useState<'draft' | 'Pending' | 'Approved'>('draft');
  const navigate = useNavigate();

  useEffect(() => {
    fetchInitialData();
    if (isEdit && id) {
      loadQuotation(parseInt(id, 10));
    }
  }, [isEdit, id]);

  const fetchInitialData = async () => {
    try {
      const [customersResponse, businessesResponse, invoicesResponse, staffResponse, taskTypesResponse, taskTemplatesResponse] = await Promise.all([
        customerService.getAll(),
        businessService.getAll().catch(() => ({ data: [] })),
        invoiceService.getAll(),
        staffService.getAll().catch(() => []),
        taskTypeService.getAll().catch(() => ({ data: [] })),
        taskTemplateService.getAll().catch(() => ({ data: [] })),
      ]);
      
      // Ensure customers are loaded with agent and team relationships
      const customersData = customersResponse.data || [];
      setCustomers(customersData);
      setBusinesses(businessesResponse.data || []);
      setInvoices(invoicesResponse.data || []);
      setStaff(Array.isArray(staffResponse) ? staffResponse : []);
      setTaskTypes(taskTypesResponse.data || []);
      setTaskTemplates(taskTemplatesResponse.data || []);
    } catch (error) {
      console.error('Failed to load initial data:', error);
      message.error('Failed to load initial data');
    }
  };

  const loadQuotation = async (quotationId: number) => {
    setLoadingQuotation(true);
    try {
      const quotation = await quotationService.getById(quotationId);
      console.log('🔍 [QuotationForm] Loaded quotation data:', quotation);
      console.log('🔍 [QuotationForm] Quotation status from API:', quotation.status);
      console.log('🔍 [QuotationForm] Quotation status type:', typeof quotation.status);
      const status = quotation.status || 'draft';
      const quotationType = quotation.type || 'personal';
      const referenceId = quotation.reference_id ?? undefined;
      form.setFieldsValue({
        ...quotation,
        customer_id: quotationType === 'personal' ? referenceId ?? quotation.customer_id : quotation.customer_id,
        business_id: quotationType === 'business' ? referenceId : undefined,
        date: dayjs(quotation.date),
        is_sent: quotation.is_sent || false,
        status: status,
      });
      setCustomerType(quotationType);
      setCurrentStatus(status as 'draft' | 'Pending' | 'Approved');
      console.log('🔍 [QuotationForm] Form status after setFieldsValue:', form.getFieldValue('status'));
      // Load quotation items
      setQuotationItems(quotation.items || []);
      // Set quotation name immediately after loading
      setQuotationName(quotation.quotation_number || '');
      // Load tasks - Commented out
      // setTasks(quotation.tasks || []);
      if (quotationType === 'business' && referenceId) {
        try {
          const business = await businessService.getById(referenceId);
          setSelectedBusiness(business);
          form.setFieldsValue({ business_id: business.id });
          if (business.customer_id) {
            const customer = await customerService.getById(business.customer_id);
            setSelectedCustomer(customer);
            form.setFieldsValue({ customer_id: business.customer_id });
            const customerExists = customers.find(c => c.id === business.customer_id);
            if (!customerExists) {
              setCustomers(prev => [...prev, customer]);
            } else {
              setCustomers(prev => prev.map(c => c.id === business.customer_id ? customer : c));
            }
          }
        } catch {
          message.error('Failed to load business details');
        }
      } else if (referenceId || quotation.customer_id) {
        const customerId = referenceId ?? quotation.customer_id;
        if (customerId) {
          try {
            const customer = await customerService.getById(customerId);
            setSelectedCustomer(customer);
            const customerExists = customers.find(c => c.id === customerId);
            if (!customerExists) {
              setCustomers(prev => [...prev, customer]);
            } else {
              setCustomers(prev => prev.map(c => c.id === customerId ? customer : c));
            }
          } catch {
            const business = businesses.find(b => b.customer_id === customerId);
            if (business) {
              setSelectedBusiness(business);
              setCustomerType('business');
              form.setFieldsValue({ business_id: business.id });
            }
          }
        }
      }
    } catch {
      message.error('Failed to load quotation');
      navigate('/quotations');
    } finally {
      setLoadingQuotation(false);
    }
  };

  const handleCustomerTypeChange = (type: 'personal' | 'business') => {
    setCustomerType(type);
    setSelectedCustomer(null);
    setSelectedBusiness(null);
    form.setFieldsValue({
      customer_id: undefined,
      business_id: undefined,
      agent_id: undefined,
      team_id: undefined,
    });
  };

  const handleCustomerChange = async (customerId: number) => {
    if (customerId) {
      try {
        const customer = await customerService.getById(customerId);
        setSelectedCustomer(customer);
        form.setFieldsValue({
          agent_id: customer.agent_id || undefined,
          team_id: customer.team_id || undefined,
        });
        // Update customers list to include the full customer data with agent and team
        setCustomers(prev => {
          const existing = prev.find(c => c.id === customerId);
          if (existing) {
            return prev.map(c => c.id === customerId ? customer : c);
          }
          return [...prev, customer];
        });
      } catch {
        message.error('Failed to load customer details');
      }
    } else {
      setSelectedCustomer(null);
      form.setFieldsValue({
        agent_id: undefined,
        team_id: undefined,
      });
    }
  };

  const handleBusinessChange = async (businessId: number) => {
    if (businessId) {
      try {
        const business = await businessService.getById(businessId);
        setSelectedBusiness(business);
        if (business.customer_id) {
          const customer = await customerService.getById(business.customer_id);
          setSelectedCustomer(customer);
          form.setFieldsValue({
            customer_id: business.customer_id,
            agent_id: customer.agent_id || business.agent_id || undefined,
            team_id: customer.team_id || undefined,
          });
        } else {
          form.setFieldsValue({
            agent_id: business.agent_id || undefined,
            team_id: undefined,
          });
        }
      } catch {
        message.error('Failed to load business details');
      }
    } else {
      setSelectedBusiness(null);
      form.setFieldsValue({
        customer_id: undefined,
        agent_id: undefined,
        team_id: undefined,
      });
    }
  };

  const handleStaffChange = async (staffId?: number) => {
    if (!staffId) {
      form.setFieldsValue({ team_id: undefined });
      return;
    }

    const selected = staff.find((member) => member.id === staffId);
    if (selected?.team_id) {
      form.setFieldsValue({ team_id: selected.team_id });
      return;
    }

    try {
      const staffMember = await staffService.getById(staffId);
      if (staffMember.team_id) {
        form.setFieldsValue({ team_id: staffMember.team_id });
      }
    } catch {
      message.error('Failed to load staff details');
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to delete this quotation?')) return;
    
    try {
      await quotationService.delete(parseInt(id, 10));
      message.success('Quotation deleted');
      navigate('/quotations');
    } catch {
      message.error('Failed to delete quotation');
    }
  };

  const handleGeneratePdf = async () => {
    if (!id) return;
    setGeneratingPdf(true);
    try {
      const pdfBlob = await quotationService.getPdf(parseInt(id, 10));
      const fileName = `${form.getFieldValue('quotation_number') || 'quotation'}.pdf`;
      const url = window.URL.createObjectURL(new Blob([pdfBlob], { type: 'application/pdf' }));
      const newWindow = window.open(url);
      if (!newWindow) {
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.click();
      }
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 10000);
    } catch {
      message.error('Failed to generate quotation PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      // Determine status based on is_sent
      let finalStatus = values.status;
      if (values.is_sent === true) {
        // If is_sent is true, set status to Pending (unless already Approved)
        if (values.status !== 'Approved') {
          finalStatus = 'Pending';
        }
      } else if (values.is_sent === false && values.status === 'Pending') {
        // If is_sent is false and current status is Pending, change to draft
        finalStatus = 'draft';
      }

      let businessCustomerId = values.customer_id;
      if (values.business_id) {
        const matchingBusiness = selectedBusiness?.id === values.business_id
          ? selectedBusiness
          : businesses.find(b => b.id === values.business_id);
        businessCustomerId = matchingBusiness?.customer_id || businessCustomerId;

        if (!businessCustomerId) {
          const business = await businessService.getById(values.business_id);
          setSelectedBusiness(business);
          businessCustomerId = business.customer_id || businessCustomerId;
        }
      }

      const payload: CreateQuotationData = {
        ...values,
        date: values.date.format('YYYY-MM-DD'),
        // If business_id is selected, use business's customer_id
        customer_id: values.business_id ? businessCustomerId : values.customer_id,
        type: customerType,
        reference_id: values.business_id ? values.business_id : values.customer_id,
        // Remove quotation_number from payload - backend will generate it
        quotation_number: undefined,
        is_sent: values.is_sent !== undefined ? values.is_sent : false,
        status: finalStatus, // Use the determined status
        items: quotationItems.map((item, index) => ({
          id: typeof item.id === 'number' ? item.id : undefined,
          task_type_id: item.task_type_id,
          year: item.year,
          task_template_id: item.task_template_id,
          unit: item.unit,
          price_per_unit: item.price_per_unit || 0,
          discount: item.discount || 0,
          amount: item.amount || 0,
          sort_order: index,
        })),
      };
      // Remove business_id from payload as backend doesn't expect it
      delete (payload as any).business_id;

      if (isEdit && id) {
        await quotationService.update(parseInt(id, 10), payload);
        message.success('Quotation updated successfully');
      } else {
        const result = await quotationService.create(payload);
        // Update form with generated quotation number
        if (result.quotation_number) {
          form.setFieldsValue({ quotation_number: result.quotation_number });
        }
        message.success('Quotation created successfully');
      }
      
      navigate('/quotations');
    } catch {
      message.error(isEdit ? 'Failed to update quotation' : 'Failed to create quotation');
    } finally {
      setSubmitting(false);
    }
  };

  const loadNextNumber = async (date?: string) => {
    try {
      console.log('Loading next quotation number for date:', date);
      const nextNumber = await quotationService.getNextNumber(date);
      console.log('Received next number:', nextNumber);
      if (nextNumber) {
        form.setFieldsValue({ quotation_number: nextNumber });
      } else {
        console.warn('No next number received, using fallback');
        // If API fails, use a fallback preview
        const prefix = 'QT';
        const year = new Date().getFullYear().toString().slice(-2);
        const month = (new Date().getMonth() + 1).toString().padStart(2, '0');
        form.setFieldsValue({ quotation_number: `${prefix}${year}${month}-001` });
      }
    } catch (error) {
      console.error('Failed to load next quotation number:', error);
      // If API fails, use a fallback preview
      const prefix = 'QT';
      const year = new Date().getFullYear().toString().slice(-2);
      const month = (new Date().getMonth() + 1).toString().padStart(2, '0');
      form.setFieldsValue({ quotation_number: `${prefix}${year}${month}-001` });
    }
  };

  useEffect(() => {
    if (!isEdit) {
      const initialDate = dayjs();
      form.setFieldsValue({
        date: initialDate,
      });
      // Load next number from backend
      loadNextNumber(initialDate.format('YYYY-MM-DD'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit]);

  // Update number when date changes
  const handleDateChange = (date: any) => {
    if (!isEdit && date) {
      loadNextNumber(date.format('YYYY-MM-DD'));
    }
  };


  const getQuotationDisplayName = () => {
    if (isEdit && id) {
      return form.getFieldValue('quotation_number') || '';
    }
    return 'New Quotation';
  };

  useEffect(() => {
    if (isEdit && id && (customers.length > 0 || selectedCustomer) && taskTypes.length > 0) {
      const displayName = getQuotationDisplayName();
      setQuotationName(displayName);
    }
  }, [isEdit, id, customers, selectedCustomer, taskTypes, form.getFieldValue('quotation_number'), form.getFieldValue('customer_id'), form.getFieldValue('task_type_id')]);

  const statuses: Array<'draft' | 'Pending' | 'Approved'> = ['draft', 'Pending', 'Approved'];
  const statusConfig = {
    draft: { bg: '#E6F7FF', border: '#40A9FF', color: '#1890ff', dot: '#1890ff', label: 'Draft' },
    Pending: { bg: '#FFF7E6', border: '#FFA940', color: '#FF8C00', dot: '#FF8C00', label: 'Pending' },
    Approved: { bg: '#F6FFED', border: '#73D13D', color: '#52C41A', dot: '#52C41A', label: 'Approved' },
  };

  return (
    <div>
      {/* Header */}
        <div style={{
          marginBottom: 24,
          paddingBottom: 16,
          borderBottom: '1px solid #f0f0f0',
        }}>
          {/* First row: Title and buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
            marginBottom: 12,
            paddingBottom: 12,
          borderBottom: '1px solid #f0f0f0',
        }}>
          <Title level={2} style={{ margin: 0, color: '#1890ff', fontSize: 28, fontWeight: 600 }}>
            {isEdit ? (quotationName || 'Loading...') : 'New Quotation'}
          </Title>
          <Space>
              {isEdit && (
                <Button onClick={handleGeneratePdf} loading={generatingPdf}>
                  Generate PDF
                </Button>
              )}
              {isEdit && !isEditing && canUpdate && (
                <Button onClick={() => setIsEditing(true)}>
                  Edit
                </Button>
              )}
              {isEdit && canDelete && (
                <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
                  Delete
                </Button>
              )}
            </Space>
          </div>
          {/* Second row: Status badges, aligned to the right */}
          {isEdit && (
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}>
                  <Space size={8}>
                    {statuses.map((status) => {
                      const isActive = currentStatus === status;
                      const config = statusConfig[status];
                      return (
                        <div
                          key={status}
                          style={{
                            padding: '4px 12px',
                            borderRadius: '12px',
                            backgroundColor: isActive ? config.bg : '#f5f5f5',
                            border: isActive ? `1px solid ${config.border}` : '1px solid #d9d9d9',
                            color: isActive ? config.color : '#595959',
                            fontWeight: isActive ? 500 : 400,
                            fontSize: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            transition: 'all 0.2s',
                          }}
                        >
                          <span style={{
                            width: 4,
                            height: 4,
                            borderRadius: '50%',
                            backgroundColor: isActive ? config.dot : '#8c8c8c',
                          }}></span>
                          {config.label}
                        </div>
                      );
                    })}
                  </Space>
        </div>
      )}
        </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        disabled={loadingQuotation || (isEdit && !isEditing)}
        initialValues={{
          status: 'draft',
        }}
        onValuesChange={(changedValues) => {
          if (changedValues.customer_id !== undefined || changedValues.task_type_id !== undefined || changedValues.quotation_number !== undefined) {
            const displayName = getQuotationDisplayName();
            setQuotationName(displayName);
          }
          if (changedValues.status !== undefined) {
            setCurrentStatus(changedValues.status as 'draft' | 'Pending' | 'Approved');
          }
        }}
      >
        {/* Hidden field to register status in form */}
        <Form.Item name="status" hidden>
          <Input type="hidden" />
        </Form.Item>
        <Row gutter={24}>
          {/* Left Column */}
            <Col span={12}>
            <Card title="Customer Information" style={{ marginBottom: 16 }} bodyStyle={{ padding: 0 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {/* Customer/Business - 单独一行，合并后三列 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>{customerType === 'personal' ? 'Customer' : 'Business'}</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      {isEditing ? (
                        customerType === 'personal' ? (
                          <Form.Item
                            name="customer_id"
                            rules={[{ required: true, message: 'Please select customer' }]}
                            style={{ margin: 0 }}
                          >
                            <Select
                              placeholder="Select Customer"
                              allowClear
                              showSearch
                              optionFilterProp="children"
                              onChange={handleCustomerChange}
                            >
                              {customers.map((customer) => (
                                <Select.Option key={customer.id} value={customer.id}>
                                  {customer.name}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        ) : (
                          <Form.Item
                            name="business_id"
                            rules={[{ required: true, message: 'Please select business' }]}
                            style={{ margin: 0 }}
                          >
                            <Select
                              placeholder="Select Business"
                              allowClear
                              showSearch
                              optionFilterProp="children"
                              onChange={handleBusinessChange}
                            >
                              {businesses.map((business) => (
                                <Select.Option key={business.id} value={business.id}>
                                  {business.name}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        )
                      ) : (
                        <div>
                          {customerType === 'personal'
                            ? (selectedCustomer?.name || form.getFieldValue('customer_id') ? customers.find(c => c.id === form.getFieldValue('customer_id'))?.name || '-' : '-')
                            : (selectedBusiness?.name || form.getFieldValue('business_id') ? businesses.find(b => b.id === form.getFieldValue('business_id'))?.name || '-' : '-')
                          }
                        </div>
                      )}
                    </td>
                  </tr>
                  {/* Customer Type - 单独一行，合并后三列 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Type</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      {isEditing ? (
                        <Space>
                          <Button
                            type={customerType === 'personal' ? 'primary' : 'default'}
                            onClick={() => handleCustomerTypeChange('personal')}
                            size="small"
                          >
                            Customer
                          </Button>
                          <Button
                            type={customerType === 'business' ? 'primary' : 'default'}
                            onClick={() => handleCustomerTypeChange('business')}
                            size="small"
                          >
                            Business
                          </Button>
                        </Space>
                      ) : (
                        <div>{customerType === 'personal' ? 'Customer' : 'Business'}</div>
                      )}
                    </td>
                  </tr>
                  {/* Address - 单独一行 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Address</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                          <Form.Item shouldUpdate={(prevValues, currentValues) => 
                            prevValues.customer_id !== currentValues.customer_id || 
                            prevValues.business_id !== currentValues.business_id
                          } noStyle>
                            {({ getFieldValue }) => {
                              const customerId = getFieldValue('customer_id');
                              const businessId = getFieldValue('business_id');
                              let addressText = '';
                              
                              if (customerId && selectedCustomer?.contacts && selectedCustomer.contacts.length > 0) {
                                const contact = selectedCustomer.contacts[0];
                                const addressParts: string[] = [];
                                if (contact.address1) addressParts.push(contact.address1);
                                if (contact.address2) addressParts.push(contact.address2);
                                addressText = addressParts.join('\n');
                              } else if (businessId && selectedBusiness?.contacts && selectedBusiness.contacts.length > 0) {
                                const contact = selectedBusiness.contacts[0];
                                const addressParts: string[] = [];
                                if (contact.main_address1) addressParts.push(contact.main_address1);
                                if (contact.main_address2) addressParts.push(contact.main_address2);
                                addressText = addressParts.join('\n');
                              }
                              
                          return isEditing ? (
                                <TextArea 
                                  disabled 
                                  value={addressText}
                                  rows={3}
                                  autoSize={{ minRows: 3, maxRows: 3 }}
                              style={{ margin: 0 }}
                                />
                          ) : (
                            <div style={{ whiteSpace: 'pre-line' }}>{addressText || '-'}</div>
                              );
                            }}
                          </Form.Item>
                    </td>
                  </tr>
                  {/* Email - 单独一行 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Email</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                          <Form.Item shouldUpdate={(prevValues, currentValues) => 
                            prevValues.customer_id !== currentValues.customer_id || 
                            prevValues.business_id !== currentValues.business_id
                          } noStyle>
                            {({ getFieldValue }) => {
                              const customerId = getFieldValue('customer_id');
                              const businessId = getFieldValue('business_id');
                              let attention = '';
                              
                              if (customerId && selectedCustomer) {
                                attention = selectedCustomer.name || '';
                              } else if (businessId && selectedBusiness) {
                                attention = selectedBusiness.name || '';
                              }
                              
                          return isEditing ? (
                                <Input 
                                  disabled 
                                  value={attention}
                              style={{ margin: 0 }}
                                />
                          ) : (
                            <div>{attention || '-'}</div>
                              );
                            }}
                          </Form.Item>
                    </td>
                  </tr>
                  {/* Contact - 单独一行 */}
                  <tr>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Contact</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                          <Form.Item shouldUpdate={(prevValues, currentValues) => 
                            prevValues.customer_id !== currentValues.customer_id || 
                            prevValues.business_id !== currentValues.business_id
                          } noStyle>
                            {({ getFieldValue }) => {
                              const customerId = getFieldValue('customer_id');
                              const businessId = getFieldValue('business_id');
                              let contact = '';
                              
                              if (customerId && selectedCustomer?.contacts && selectedCustomer.contacts.length > 0) {
                                const contactData = selectedCustomer.contacts[0];
                                contact = contactData.contact_number || contactData.email || '';
                              } else if (businessId && selectedBusiness?.contacts && selectedBusiness.contacts.length > 0) {
                                const contactData = selectedBusiness.contacts[0];
                                contact = contactData.contact_number || contactData.email || '';
                              }
                              
                          return isEditing ? (
                                <Input 
                                  disabled 
                                  value={contact}
                              style={{ margin: 0 }}
                                />
                          ) : (
                            <div>{contact || '-'}</div>
                              );
                            }}
                          </Form.Item>
                    </td>
                  </tr>
                </tbody>
              </table>
            </Card>
            </Col>

          {/* Right Column */}
            <Col span={12}>
            <Card title="Quotation Details" style={{ marginBottom: 16 }} bodyStyle={{ padding: 0 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {/* Quotation No. - 单独一行 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Quotation No.</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      {isEditing ? (
                        <Form.Item name="quotation_number" style={{ margin: 0 }}>
                          <Input placeholder="Auto-generated" />
                        </Form.Item>
                      ) : (
                        <div>{form.getFieldValue('quotation_number') || '-'}</div>
                      )}
                    </td>
                  </tr>
                  {/* Invoice No. - 单独一行 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Invoice No.</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                          <Form.Item name="invoice_id" noStyle hidden>
                            <Input type="hidden" />
                          </Form.Item>
                          <Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.invoice_id !== currentValues.invoice_id} noStyle>
                            {({ getFieldValue }) => {
                              const invoiceId = getFieldValue('invoice_id');
                              const invoice = invoices.find(i => i.id === invoiceId);
                          return isEditing ? (
                                <Input 
                                  placeholder="Automation after conversion" 
                                  disabled 
                                  value={invoice?.invoice_number || ''}
                              style={{ margin: 0 }}
                                />
                          ) : (
                            <div>{invoice?.invoice_number || '-'}</div>
                              );
                            }}
                          </Form.Item>
                    </td>
                  </tr>
                  {/* Date - 单独一行 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Date</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      {isEditing ? (
                        <Form.Item
                          name="date"
                          rules={[{ required: true, message: 'Please select date' }]}
                          style={{ margin: 0 }}
                        >
                          <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" onChange={handleDateChange} />
                        </Form.Item>
                      ) : (
                        <div>
                          {form.getFieldValue('date') 
                            ? (dayjs.isDayjs(form.getFieldValue('date')) 
                              ? form.getFieldValue('date').format('DD/MM/YYYY')
                              : dayjs(form.getFieldValue('date')).format('DD/MM/YYYY'))
                            : '-'
                          }
                      </div>
                      )}
                    </td>
                  </tr>
                  {/* Advisor - 单独一行 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Advisor</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                          <Form.Item name="agent_id" noStyle hidden>
                            <Input type="hidden" />
                          </Form.Item>
                          <Form.Item shouldUpdate={(prevValues, currentValues) => 
                            prevValues.customer_id !== currentValues.customer_id || 
                            prevValues.business_id !== currentValues.business_id
                          } noStyle>
                            {({ getFieldValue }) => {
                              const customerId = getFieldValue('customer_id');
                              const businessId = getFieldValue('business_id');
                              let advisorName = '';
                              
                              if (customerId && selectedCustomer?.agent) {
                                advisorName = selectedCustomer.agent.name || '';
                              } else if (businessId && selectedBusiness?.agent) {
                                advisorName = selectedBusiness.agent.name || '';
                              }
                              
                          return isEditing ? (
                                <Input 
                                  disabled 
                                  value={advisorName}
                              style={{ margin: 0 }}
                                />
                          ) : (
                            <div>{advisorName || '-'}</div>
                              );
                            }}
                          </Form.Item>
                    </td>
                  </tr>
                  {/* Team - 单独一行 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Team</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                          <Form.Item name="team_id" noStyle hidden>
                            <Input type="hidden" />
                          </Form.Item>
                          <Form.Item shouldUpdate={(prevValues, currentValues) => 
                            prevValues.customer_id !== currentValues.customer_id || 
                            prevValues.business_id !== currentValues.business_id
                          } noStyle>
                            {({ getFieldValue }) => {
                              const customerId = getFieldValue('customer_id');
                              const businessId = getFieldValue('business_id');
                              const staffId = getFieldValue('staff_id');
                              let teamName = '';
                              
                              if (customerId && selectedCustomer?.team) {
                                teamName = selectedCustomer.team.name || '';
                              } else if (staffId) {
                                const staffMember = staff.find(member => member.id === staffId);
                                teamName = staffMember?.team?.name || '';
                              }
                              
                          return isEditing ? (
                                <Input 
                                  disabled 
                                  value={teamName}
                              style={{ margin: 0 }}
                                />
                          ) : (
                            <div>{teamName || '-'}</div>
                              );
                            }}
                          </Form.Item>
                    </td>
                  </tr>
                  {/* Staff - 单独一行 */}
                  <tr>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Staff</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      {isEditing ? (
                        <Form.Item name="staff_id" style={{ margin: 0 }}>
                          <Select
                            placeholder="Select Staff"
                            allowClear
                            showSearch
                            optionFilterProp="children"
                            onChange={handleStaffChange}
                          >
                            {staff.map((user) => (
                              <Select.Option key={user.id} value={user.id}>
                                {user.name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      ) : (
                        <div>
                          {form.getFieldValue('staff_id') 
                            ? (staff.find(s => s.id === form.getFieldValue('staff_id'))?.name || '-')
                            : '-'
                          }
                      </div>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </Card>
          </Col>
        </Row>

        {/* Bottom Section */}
        <Card title="Task Information" style={{ marginBottom: 24 }} bodyStyle={{ padding: '16px' }}>
          <Table
            columns={[
              {
                title: 'Type',
                dataIndex: 'task_type_id',
                key: 'task_type_id',
                width: '15%',
                render: (taskTypeId: number, record: any, index: number) => {
                  if (isEditing) {
                    return (
                      <Select
                        placeholder="Select Type"
                        allowClear
                        value={taskTypeId}
                        onChange={(value) => {
                          const updated = [...quotationItems];
                          updated[index] = { ...updated[index], task_type_id: value };
                          setQuotationItems(updated);
                        }}
                        style={{ width: '100%' }}
                      >
                        {taskTypes.map((type) => (
                          <Select.Option key={type.id} value={type.id}>
                            {type.name}
                          </Select.Option>
                        ))}
                      </Select>
                    );
                  }
                  const taskType = taskTypes.find(t => t.id === taskTypeId);
                  return <span>{taskType?.name || '-'}</span>;
                },
              },
              {
                title: 'Year',
                dataIndex: 'year',
                key: 'year',
                width: '10%',
                render: (year: number, record: any, index: number) => {
                  if (isEditing) {
                    return (
                      <Select
                        placeholder="Select Year"
                        allowClear
                        value={year}
                        onChange={(value) => {
                          const updated = [...quotationItems];
                          updated[index] = { ...updated[index], year: value };
                          setQuotationItems(updated);
                        }}
                        style={{ width: '100%' }}
                      >
                        {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i).map((y) => (
                          <Select.Option key={y} value={y}>
                            {y}
                          </Select.Option>
                        ))}
                      </Select>
                    );
                  }
                  return <span>{year || '-'}</span>;
                },
              },
              {
                title: 'Task No.',
                dataIndex: 'task_template_id',
                key: 'task_no',
                width: '12%',
                render: (taskTemplateId: number) => {
                  const taskTemplate = taskTemplates.find(t => t.id === taskTemplateId);
                  return <span>{taskTemplate ? `Task #${taskTemplate.id}` : '-'}</span>;
                },
              },
              {
                title: 'Task',
                dataIndex: 'task_template_id',
                key: 'task_template_id',
                width: '18%',
                render: (taskTemplateId: number, record: any, index: number) => {
                  if (isEditing) {
                    return (
                      <Select
                        placeholder="Select Task"
                        allowClear
                        value={taskTemplateId}
                        onChange={(value) => {
                          const updated = [...quotationItems];
                          updated[index] = { ...updated[index], task_template_id: value };
                          setQuotationItems(updated);
                        }}
                        style={{ width: '100%' }}
                      >
                        {taskTemplates.map((template) => (
                          <Select.Option key={template.id} value={template.id}>
                            {template.name}
                          </Select.Option>
                        ))}
                      </Select>
                    );
                  }
                  const taskTemplate = taskTemplates.find(t => t.id === taskTemplateId);
                  return <span>{taskTemplate?.name || '-'}</span>;
                },
              },
              {
                title: 'Unit',
                dataIndex: 'unit',
                key: 'unit',
                width: '10%',
                render: (unit: number | string, record: any, index: number) => {
                  if (isEditing) {
                    return (
                      <InputNumber
                        value={typeof unit === 'number' ? unit : (unit ? parseFloat(unit) : 0)}
                        onChange={(value) => {
                          const updated = [...quotationItems];
                          const unitValue = value || 0;
                          updated[index] = { 
                            ...updated[index], 
                            unit: unitValue,
                            amount: ((record.price_per_unit || 0) * unitValue) - (record.discount || 0),
                          };
                          setQuotationItems(updated);
                        }}
                        placeholder="Unit"
                        style={{ width: '100%' }}
                        min={0}
                        precision={2}
                      />
                    );
                  }
                  return <span>{unit || '-'}</span>;
                },
              },
              {
                title: 'Price/Unit',
                dataIndex: 'price_per_unit',
                key: 'price_per_unit',
                width: '12%',
                render: (pricePerUnit: number, record: any, index: number) => {
                  if (isEditing) {
                    return (
                      <InputNumber
                        value={pricePerUnit || 0}
                        onChange={(value) => {
                          const updated = [...quotationItems];
                          const price = value || 0;
                          const unit = typeof record.unit === 'number' ? record.unit : parseFloat(record.unit || '0');
                          updated[index] = { 
                            ...updated[index], 
                            price_per_unit: price,
                            amount: (price * unit) - (record.discount || 0),
                          };
                          setQuotationItems(updated);
                        }}
                        style={{ width: '100%' }}
                        min={0}
                        precision={2}
                      />
                    );
                  }
                  const numValue = typeof pricePerUnit === 'number' ? pricePerUnit : parseFloat(pricePerUnit || '0');
                  return <span>{!isNaN(numValue) ? numValue.toFixed(2) : '0.00'}</span>;
                },
              },
              {
                title: 'Discount',
                dataIndex: 'discount',
                key: 'discount',
                width: '10%',
                render: (discount: number, record: any, index: number) => {
                  if (isEditing) {
                    return (
                      <InputNumber
                        value={discount || 0}
                        onChange={(value) => {
                          const updated = [...quotationItems];
                          const disc = value || 0;
                          const unit = typeof record.unit === 'number' ? record.unit : parseFloat(record.unit || '0');
                          updated[index] = { 
                            ...updated[index], 
                            discount: disc,
                            amount: ((record.price_per_unit || 0) * unit) - disc,
                          };
                          setQuotationItems(updated);
                        }}
                        style={{ width: '100%' }}
                        min={0}
                        precision={2}
                      />
                    );
                  }
                  const numValue = typeof discount === 'number' ? discount : parseFloat(discount || '0');
                  return <span>{!isNaN(numValue) ? numValue.toFixed(2) : '0.00'}</span>;
                },
              },
              {
                title: 'Amount',
                dataIndex: 'amount',
                key: 'amount',
                width: '10%',
                render: (amount: number, record: any, index: number) => {
                  if (isEditing) {
                    return (
                      <InputNumber
                        value={amount || 0}
                        onChange={(value) => {
                          const updated = [...quotationItems];
                          updated[index] = { ...updated[index], amount: value || 0 };
                          setQuotationItems(updated);
                        }}
                        style={{ width: '100%' }}
                        min={0}
                        precision={2}
                      />
                    );
                  }
                  const numValue = typeof amount === 'number' ? amount : parseFloat(amount || '0');
                  return <span>{!isNaN(numValue) ? numValue.toFixed(2) : '0.00'}</span>;
                },
              },
              ...(isEditing ? [{
                title: 'Action',
                key: 'action',
                width: '3%',
                render: (_: any, record: any, index: number) => (
                  <Button
                    type="link"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      setQuotationItems(quotationItems.filter((_, i) => i !== index));
                    }}
                  />
                ),
              }] : []),
            ]}
            dataSource={quotationItems.map((item, index) => ({ ...item, key: index }))}
            pagination={false}
            size="small"
          />
          {isEditing && (
            <div style={{ marginTop: 12 }}>
              <Button
                type="link"
                style={{ color: '#ff4d4f', padding: 0 }}
                icon={<PlusOutlined />}
                onClick={() => {
                  setQuotationItems([...quotationItems, {
                    task_type_id: undefined,
                    year: undefined,
                    task_template_id: undefined,
                    unit: 0,
                    price_per_unit: 0,
                    discount: 0,
                    amount: 0,
                  }]);
                }}
              >
                Add Item
              </Button>
            </div>
          )}
        </Card>


        <div style={{ 
          padding: '16px 24px', 
          borderTop: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 24,
          position: 'sticky',
          bottom: 0,
          backgroundColor: '#fff',
          zIndex: 10,
          
        }}>
        <div>
          <Form.Item shouldUpdate noStyle>
            {({ getFieldValue }) => {
              const currentStatus = getFieldValue('status');
              const isSent = getFieldValue('is_sent') || false;
              // Disable Sent checkbox if status is Approved, or if not editing
              const isSentDisabled = (!isEditing && isEdit) || currentStatus === 'Approved';
              
              return (
                <Form.Item name="is_sent" valuePropName="checked" noStyle>
                  <Checkbox 
                    onChange={(e) => {
                      if (!isEditing) return; // Don't allow changes in view mode
                      const checked = e.target.checked;
                      // Form.Item will automatically update is_sent
                      // We just need to update status
                      const currentFormStatus = form.getFieldValue('status');
                      if (checked) {
                        form.setFieldsValue({ status: 'Pending' });
                        setCurrentStatus('Pending');
                      } else {
                        // If unchecking and not approved yet, revert to draft
                        if (currentFormStatus !== 'Approved') {
                          form.setFieldsValue({ status: 'draft' });
                          setCurrentStatus('draft');
                        }
                      }
                    }}
                    disabled={isSentDisabled}
                  >
                    Sent
                  </Checkbox>
                </Form.Item>
              );
            }}
          </Form.Item>
        </div>
        <Space>
          {isEditing && (
            <>
              <Button onClick={() => {
                if (isEdit && id) {
                  setIsEditing(false);
                  // Reload quotation data to reset form
                  loadQuotation(parseInt(id, 10));
                } else {
                  // For create mode, navigate back
                  navigate('/quotations');
                }
              }}>
                Cancel
              </Button>
              <Button 
                type="primary" 
                onClick={() => {
                  form.submit();
                }} 
                loading={submitting}
              >
                {isEdit ? 'Save' : 'Create'}
              </Button>
            </>
          )}
          {!isEditing && isEdit && (
            <Button 
              onClick={() => navigate('/quotations')}
              disabled={false}
              style={{ pointerEvents: 'auto' }}
            >
              Cancel
            </Button>
          )}
        </Space>
      </div>
      </Form>
    </div>
  );
};
